import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

interface StudentSidebarContextType {
    isDesktopCollapsed: boolean;
    setIsDesktopCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
    toggleDesktopSidebar: () => void;
    isMobileOpen: boolean;
    setIsMobileOpen: React.Dispatch<React.SetStateAction<boolean>>;
    toggleMobileSidebar: () => void;
}

const StudentSidebarContext = createContext<StudentSidebarContextType | undefined>(undefined);

export function StudentSidebarProvider({ children }: { children: React.ReactNode }) {
    const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);

    const toggleDesktopSidebar = useCallback(() => {
        setIsDesktopCollapsed((prev) => !prev);
    }, []);

    const toggleMobileSidebar = useCallback(() => {
        setIsMobileOpen((prev) => !prev);
    }, []);

    // Listen to custom window events for loose coupling
    useEffect(() => {
        const handleOpen = () => {
            if (typeof window !== "undefined" && window.innerWidth < 1024) {
                setIsMobileOpen(true);
            } else {
                setIsDesktopCollapsed(false);
            }
        };

        const handleToggle = () => {
            if (typeof window !== "undefined" && window.innerWidth < 1024) {
                setIsMobileOpen((prev) => !prev);
            } else {
                setIsDesktopCollapsed((prev) => !prev);
            }
        };

        const handleCollapse = () => {
            setIsDesktopCollapsed(true);
        };

        const handleExpand = () => {
            setIsDesktopCollapsed(false);
        };

        window.addEventListener("open-student-sidebar", handleOpen);
        window.addEventListener("toggle-student-sidebar", handleToggle);
        window.addEventListener("collapse-student-sidebar", handleCollapse);
        window.addEventListener("expand-student-sidebar", handleExpand);

        return () => {
            window.removeEventListener("open-student-sidebar", handleOpen);
            window.removeEventListener("toggle-student-sidebar", handleToggle);
            window.removeEventListener("collapse-student-sidebar", handleCollapse);
            window.removeEventListener("expand-student-sidebar", handleExpand);
        };
    }, []);

    const value = useMemo(
        () => ({
            isDesktopCollapsed,
            setIsDesktopCollapsed,
            toggleDesktopSidebar,
            isMobileOpen,
            setIsMobileOpen,
            toggleMobileSidebar,
        }),
        [isDesktopCollapsed, toggleDesktopSidebar, isMobileOpen, toggleMobileSidebar]
    );

    return (
        <StudentSidebarContext.Provider value={value}>
            {children}
        </StudentSidebarContext.Provider>
    );
}

export function useStudentSidebar(): StudentSidebarContextType {
    const context = useContext(StudentSidebarContext);
    if (!context) {
        // Fallback if rendered outside provider
        return {
            isDesktopCollapsed: false,
            setIsDesktopCollapsed: () => {},
            toggleDesktopSidebar: () => {
                if (typeof window !== "undefined") {
                    window.dispatchEvent(new Event("toggle-student-sidebar"));
                }
            },
            isMobileOpen: false,
            setIsMobileOpen: () => {},
            toggleMobileSidebar: () => {
                if (typeof window !== "undefined") {
                    window.dispatchEvent(new Event("open-student-sidebar"));
                }
            },
        };
    }
    return context;
}
